'use client';
// Клієнтські presentational-шматки PDP, що залежать від мови — рендеряться через
// контекст (usePriceConvert.lang), тож перемикання EN/RU/UA миттєве, без чекання
// на router.refresh() серверного компонента.
import Link from 'next/link';
import { Shield, CheckCircle, Zap, Package, ChevronRight } from 'lucide-react';
import { usePriceConvert } from '@/lib/PriceContext';
import { useT } from '@/hooks/useT';

export function PdpBreadcrumb({ titleEn, titleRu, titleUk, catEn, catRu }: {
  titleEn: string; titleRu: string | null; titleUk: string | null;
  catEn: string | null; catRu: string | null;
}) {
  const { lang } = usePriceConvert();
  const t = useT();
  const title = lang === 'ru' ? (titleRu || titleEn) : lang === 'uk' ? (titleUk || titleEn) : titleEn;
  const cat = catEn ? (lang === 'ru' ? (catRu || catEn) : catEn) : null;
  return (
    <div className="pdp-breadcrumb" style={{ maxWidth: 1560, margin: '0 auto', padding: '14px 24px 0', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--v4-fg-3)' }}>
      <Link href="/catalog" className="pdp-breadcrumb-link" style={{ color: 'var(--v4-fg-3)', textDecoration: 'none', flexShrink: 0 }}>{t('nav_catalog')}</Link>
      <ChevronRight size={12} style={{ opacity: .4, flexShrink: 0 }} />
      {cat && (
        <span style={{ display: 'contents' }}>
          <span style={{ whiteSpace: 'nowrap' }}>{cat}</span>
          <ChevronRight size={12} style={{ opacity: .4, flexShrink: 0 }} />
        </span>
      )}
      <span className="pdp-breadcrumb-current" style={{ color: 'var(--v4-fg-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{title}</span>
    </div>
  );
}

// "Скоро · <дата>" — рахує локалізовану дату на клієнті (миттєвий перемикач мови)
export function PdpSoon({ releaseAtISO }: { releaseAtISO: string | null }) {
  const { lang } = usePriceConvert();
  const t = useT();
  if (!releaseAtISO) return null;
  const locale = lang === 'ru' ? 'ru-RU' : lang === 'en' ? 'en-US' : 'uk-UA';
  const label = new Date(releaseAtISO).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  return <>{t('pdp_soon')} · {label}</>;
}

const GRAD: React.CSSProperties = {
  background: 'linear-gradient(90deg,#52b043,#0078d4)',
  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
};

export function PdpTitle({ titleEn, titleRu, titleUk }: { titleEn: string; titleRu: string | null; titleUk: string | null }) {
  const { lang } = usePriceConvert();
  const title = lang === 'ru' ? (titleRu || titleEn) : lang === 'uk' ? (titleUk || titleEn) : titleEn;
  const words = title.split(' ');
  return (
    <h1 className="pdp-title" style={{
      fontFamily: "'Orbitron', sans-serif",
      fontSize: 'clamp(16px,2.6vw,26px)',
      fontWeight: 900, lineHeight: 1.15, letterSpacing: '-.02em',
      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden',
      color: 'var(--v4-fg, #fff)', margin: 0,
    }}>
      {words.length === 1
        ? <span style={GRAD}>{title}</span>
        : <>{words[0]} <span style={GRAD}>{words.slice(1).join(' ')}</span></>}
    </h1>
  );
}

export function PdpNotices({ xboxLiveGold, isDlc, compact }: { xboxLiveGold: boolean; isDlc: boolean; compact?: boolean }) {
  const { lang } = usePriceConvert();
  // Компактний режим (для кошика): без заголовка, менший підзаголовок, обводка по тексту.
  const pad = compact ? '2.5px 6px' : '8px 11px';
  const gap = compact ? 4 : 9;
  const icoSz = compact ? 7 : 15; // не більший за шрифт напису
  const titleSz = 11.5;
  const subSz = compact ? 7 : 10.5;
  // У компактному режимі контейнер «обгортає» текст (не тягнеться на весь рядок).
  const box = (extra: React.CSSProperties): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap,
    padding: pad, borderRadius: compact ? 7 : 10,
    ...(compact ? { alignSelf: 'flex-start', maxWidth: '100%' } : {}),
    ...extra,
  });
  return (
    <>
      {xboxLiveGold && (
        <div className="pdp-gold-req" style={box({ border: '1px solid var(--gold-border)', background: 'var(--gold-bg)' })}>
          <span className={`pdp-gold-ico${compact ? ' pdp-ico-compact' : ''}`}><Shield size={icoSz} strokeWidth={2.2} /></span>
          <div style={{ minWidth: 0 }}>
            {!compact && (
              <span style={{ fontSize: titleSz, fontWeight: 600, color: 'var(--gold-fg)', display: 'block', lineHeight: 1.25 }}>
                {lang === 'ru' ? 'Требуется Game Pass' : lang === 'uk' ? 'Потрібен Game Pass' : 'Game Pass required'}
              </span>
            )}
            <span className="pdp-note-sub" style={{ fontSize: subSz, color: compact ? 'var(--gold-fg)' : 'var(--gold-fg-2)', lineHeight: 1.35 }}>
              {lang === 'ru'
                ? 'Онлайн-режим на консоли требует Game Pass Core или Ultimate (продаётся отдельно).'
                : lang === 'uk'
                ? 'Онлайн-режим на консолі потребує Game Pass Core або Ultimate (продається окремо).'
                : 'Console online multiplayer requires Game Pass Core or Ultimate (sold separately).'}
            </span>
          </div>
        </div>
      )}
      {isDlc && (
        <div className="pdp-basegame-req" style={box({ border: '1px solid var(--bgame-border)', background: 'var(--bgame-bg)' })}>
          <span className={`pdp-basegame-ico${compact ? ' pdp-ico-compact' : ''}`}><Package size={icoSz} strokeWidth={2.2} /></span>
          <div style={{ minWidth: 0 }}>
            {!compact && (
              <span style={{ fontSize: titleSz, fontWeight: 600, color: 'var(--bgame-fg)', display: 'block', lineHeight: 1.25 }}>
                {lang === 'ru' ? 'Требуется базовая игра' : lang === 'uk' ? 'Потрібна базова гра' : 'Base game required'}
              </span>
            )}
            <span className="pdp-note-sub" style={{ fontSize: subSz, color: compact ? 'var(--bgame-fg)' : 'var(--bgame-fg-2)', lineHeight: 1.35 }}>
              {lang === 'ru'
                ? 'Требуется игра (продаётся отдельно).'
                : lang === 'uk'
                ? 'Потрібна гра (продається окремо).'
                : 'Requires a game (sold separately).'}
            </span>
          </div>
        </div>
      )}
    </>
  );
}

export function PdpTrust() {
  const t = useT();
  const items = [
    { Icon: Shield,      labelKey: 'pdp_trust_guarantee' as const, sub: '100%',  color: '#52b043', sep: true  },
    { Icon: Zap,         labelKey: 'pdp_trust_support'   as const, sub: '24/7',  color: '#fbbf24', sep: true  },
    { Icon: CheckCircle, labelKey: 'pdp_trust_sold'      as const, sub: '2.8k+', color: '#52b043', sep: false },
  ];
  return (
    <div className="pp-trust">
      {items.map(({ Icon, labelKey, sub, color, sep }) => (
        <div key={labelKey} className={sep ? 'pp-trust-item pp-trust-item-sep' : 'pp-trust-item'}>
          <Icon size={16} color={color} />
          <span className="pp-trust-val">{sub}</span>
          <span className="pp-trust-label">{t(labelKey)}</span>
        </div>
      ))}
    </div>
  );
}
